import { TodayTasks } from "./TodayTasks";
import {
  ArrowRight,
  CalendarDays,
  CalendarPlus,
  Plus,
} from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../../auth/AuthProvider";
import {
  PageHeader,
  StatePanel,
  StatusBadge,
} from "../../components/ui";
import { useAgendaEvents, eventCategories } from "../../data/agenda-events";
import { useErpData } from "../../data/DataProvider";
import { formatDate, mexicoDateKey } from "../../data/format";
import { useOperationOverview } from "./useOperationOverview";
import { useListScroll } from "./useListScroll";
import { caseUrl, funnelUrl, isStaleAttempt, serviceFromSlug, serviceNames } from "./shared";
export function HomePage() {
  const { snapshot, status, error, refresh } = useErpData(),
    overview = useOperationOverview();
  const { hasPermission, user } = useAuth();
  const date = mexicoDateKey(),
    until = new Date(Date.parse(`${date}T12:00:00Z`) + 7 * 86400000)
      .toISOString()
      .slice(0, 10);
  const events = useAgendaEvents(date, until);
  // The service chosen in "Tus pendientes" filters the whole page.
  const [params] = useSearchParams();
  const service = serviceFromSlug(params.get("servicio"));
  useListScroll(Boolean(snapshot));
  if (status === "error")
    return (
      <StatePanel
        state="error"
        description={error ?? undefined}
        onRetry={() => void refresh()}
      />
    );
  if (!snapshot) return <StatePanel state="loading" />;
  const upcoming = overview.data?.upcoming ?? [];
  const teamEvents = (events.data ?? []).filter(
    (event) => event.status === "scheduled" && (!service || event.service === service),
  );
  // Payments and bookings to review, of this service; repeated attempts by one customer show once.
  const attempts = snapshot.attention.filter((item) => (item.kind === "payment" || item.kind === "booking") && (!service || item.service === service));
  // Attempts abandoned for more than 48 hours wait in Por cobrar, under their own filter.
  const staleAttempts = attempts.filter((item) => isStaleAttempt(item)).length;
  const review = attempts
    .filter((item) => !isStaleAttempt(item))
    .reduce<{ item: (typeof snapshot.attention)[number]; more: number }[]>((rows, item) => {
      const same = rows.find((row) => row.item.kind === item.kind && row.item.customer === item.customer);
      if (same) same.more += 1;
      else rows.push({ item, more: 0 });
      return rows;
    }, []);
  const plan = [
    ...upcoming.filter((item) => !service || item.service === service).map((item) => ({
      id: `booking:${item.id}`,
      title: item.customer,
      date: item.date,
      time: item.time,
      description: `${serviceNames[item.service]} · ${item.guests} personas`,
      service: item.service,
      status: "Reserva confirmada",
      to: caseUrl("booking", item.id, "/"),
      kind: "booking",
    })),
    ...teamEvents.map((event) => ({
      id: `event:${event.id}`,
      title: event.title,
      date: event.date,
      time: event.startTime ?? "Todo el día",
      description: eventCategories[event.category],
      service: event.service ?? "general",
      status: "Evento",
      to: `/agenda?${new URLSearchParams({ date: event.date, event: event.id })}`,
      kind: "event",
    })),
  ]
    .sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time))
    .slice(0, 6);
  return (
    <section className="workspace-home home-v2">
      <PageHeader
        title={`Hola, ${user?.name.split(" ")[0] ?? ""}`.trim()}
        description={`${new Intl.DateTimeFormat("es-MX", { weekday: "long", day: "numeric", month: "long", timeZone: "America/Mexico_City" }).format(new Date())} · lo que toca hoy, ${service ? `de ${serviceNames[service]}` : "de todos los servicios"}.`.replace(/^./, (c) => c.toUpperCase())}
        actions={
          <>
            {hasPermission("crm.write") && (
              <Link
                className="button button--secondary"
                to={`/agenda?date=${date}&create=1`}
              >
                <CalendarPlus size={18} />
                Nuevo evento
              </Link>
            )}
          </>
        }
      />
      {hasPermission("crm.read") && <TodayTasks />}
      {overview.error && (
        <StatePanel
          state="error"
          description={overview.error}
          onRetry={overview.refresh}
        />
      )}
      <div className="today-secondary">
      <section className="workspace-card">
        <header>
          <h2>Pagos y reservas por revisar</h2>
        </header>
        {review
          .slice(0, 6)
          .map(({ item, more }) => (
            <Link
              key={item.id}
              className="workspace-row home-action-row"
              to={caseUrl(
                item.kind === "payment" ? "order" : "booking",
                item.targetId,
                "/",
              )}
            >
              <div>
                <strong>{item.customer}</strong>
                <small>{item.reference}{more ? ` · y ${more} ${more === 1 ? "intento más" : "intentos más"}` : ""}{!service && item.service ? ` · ${serviceNames[item.service]}` : ""}</small>
                <StatusBadge tone={item.tone}>{item.subject}</StatusBadge>
              </div>
              <span className="row-action-label">
                {item.kind === "payment" ? "Revisar pago" : "Revisar reserva"}
                <ArrowRight size={16} />
              </span>
            </Link>
          ))}
        {!review.length && (
          <p className="workspace-empty">
            Sin pagos iniciales ni reservas pendientes en los registros
            recientes.
          </p>
        )}
        {staleAttempts > 0 && hasPermission("finance.read") && (
          <Link className="home-stale-attempts" to="/por-cobrar?ver=intentos">
            {staleAttempts === 1
              ? "1 intento de pago sin completar de hace más de 2 días"
              : `${staleAttempts} intentos de pago sin completar de hace más de 2 días`}
            <ArrowRight size={14} aria-hidden="true" />
          </Link>
        )}
        <Link className="button button--quiet" to={funnelUrl(null, "reservas")}>
          Consultar todos los pedidos
        </Link>
      </section>
      {!hasPermission("crm.read") && hasPermission("confirmations.read") && (
        <section className="workspace-card">
          <header>
            <h2>Documentos por revisar</h2>
          </header>
          <p>Consulta los intentos y el estado de las confirmaciones.</p>
          <Link className="button button--secondary" to="/confirmaciones">
            Revisar confirmaciones
          </Link>
        </section>
      )}
      <div>
        <section className="workspace-card home-upcoming">
          <header>
            <div>
              <h2>Próximos 7 días</h2>
            </div>
            <CalendarDays size={22} />
          </header>
          {plan.map((item) => (
            <Link
              key={item.id}
              className={`workspace-row home-plan-row home-plan-row--${item.service}`}
              to={item.to}
            >
              <span className="home-date-tile">
                <strong>{item.date.slice(8)}</strong>
                <small>
                  {new Intl.DateTimeFormat("es-MX", {
                    month: "short",
                    timeZone: "UTC",
                  }).format(new Date(`${item.date}T12:00:00Z`))}
                </small>
              </span>
              <div>
                <strong>{item.title}</strong>
                <small>{item.description}</small>
                <StatusBadge tone={item.kind === "event" ? "info" : "success"}>
                  {item.status}
                </StatusBadge>
              </div>
              <span className="workspace-date-label">
                {item.time}
                <small>{formatDate(item.date)}</small>
              </span>
              <ArrowRight size={16} />
            </Link>
          ))}
          {((!overview.data && !overview.error) ||
            (events.data === null && !events.error)) && (
            <StatePanel state="loading" />
          )}
          {events.error && (
            <StatePanel
              state="error"
              description={events.error}
              onRetry={events.refresh}
            />
          )}
          {overview.data && events.data && !plan.length && (
            <div className="home-clear-state">
              <CalendarPlus size={28} />
              <strong>Organiza lo que viene</strong>
              <p>
                Las reservas confirmadas y los eventos que agregues aparecerán
                aquí.
              </p>
            </div>
          )}
          <footer className="home-card-actions">
            <Link className="button button--secondary" to="/agenda">
              Abrir calendario
              <ArrowRight size={16} />
            </Link>
            {hasPermission("crm.write") && (
              <Link
                className="workspace-text-link"
                to={`/agenda?date=${date}&create=1`}
              >
                <Plus size={17} />
                Agregar evento
              </Link>
            )}
          </footer>
        </section>
      </div>
      </div>
    </section>
  );
}
